import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import {
  FigureMode,
  getFigureNotesMode,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/figure-mode';
import {
  detailsModalCommonHorizontalPadding,
  detailsModalCommonVerticalPadding,
} from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/common/styles';
import { ImpactSpecificFigureBoxes } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/comparisons';
import { BoxesProps } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/figure-boxes';
import { SingleImpactInfoBoxMonetaryFigureBoxes } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/monetary/monetary-figure-boxes';
import { ComparisonFigureNotes } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/notes/comparison-notes';
import { MonetaryFigureNotes } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/notes/monetary-notes';
import { ValueChainFigureNotes } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/notes/value-chain-figure-notes';
import { TopValueChain } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/top-value-chain';
import styled from 'styled-components';

export function FigureBoxesDesktop(props: BoxesProps) {
  const { comparisonBoxCount, valueChainBoxCount, monetaryBoxCount, totalBoxCount } = props;
  const maxWidth = `${totalBoxCount * 20}em`;

  const context = useNetImpactLensContext();
  const [valence, impact] = [context?.state.valence, context?.state.impact];

  if (!impact || !valence) {
    return <></>;
  }

  const notesMode = getFigureNotesMode(impact, valence);
  const shouldJoinNotes =
    notesMode === FigureMode.ONLY_COMPARISON_NOTES || notesMode === FigureMode.ONLY_MONETARY_NOTES;

  return (
    <Root style={{ maxWidth }}>
      <FigureAndExplanationPart>
        <BoxWrap style={{ flexBasis: `${(comparisonBoxCount / totalBoxCount) * 100}%` }}>
          <ImpactSpecificFigureBoxes />
        </BoxWrap>
        <BoxWrap style={{ flexBasis: `${(monetaryBoxCount / totalBoxCount) * 100}%` }}>
          <SingleImpactInfoBoxMonetaryFigureBoxes />
        </BoxWrap>
        <BoxWrap style={{ flexBasis: `${(valueChainBoxCount / totalBoxCount) * 100}%` }}>
          <TopValueChain />
        </BoxWrap>
      </FigureAndExplanationPart>
      <NotesPart>
        {shouldJoinNotes ? (
          <NotesWrap style={{ flexBasis: `${((comparisonBoxCount + monetaryBoxCount) / totalBoxCount) * 100}%` }}>
            {notesMode == FigureMode.ONLY_COMPARISON_NOTES && <ComparisonFigureNotes />}
            {notesMode == FigureMode.ONLY_MONETARY_NOTES && <MonetaryFigureNotes />}
          </NotesWrap>
        ) : (
          <>
            <NotesWrap style={{ flexBasis: `${(comparisonBoxCount / totalBoxCount) * 100}%` }}>
              <ComparisonFigureNotes />
            </NotesWrap>
            <NotesWrap style={{ flexBasis: `${(monetaryBoxCount / totalBoxCount) * 100}%` }}>
              <MonetaryFigureNotes />
            </NotesWrap>
          </>
        )}
        <NotesWrap style={{ flexBasis: `${(valueChainBoxCount / totalBoxCount) * 100}%` }}>
          <ValueChainFigureNotes />
        </NotesWrap>
      </NotesPart>
    </Root>
  );
}

const Root = styled.div`
  ${getTextStyle({ size: 'xxs' })}
  width: 100%;
`;

const FigureAndExplanationPart = styled.div`
  display: flex;
`;

const NotesPart = styled.div`
  display: flex;
`;

const BoxWrap = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
`;

const NotesWrap = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  ${detailsModalCommonVerticalPadding};
  ${detailsModalCommonHorizontalPadding};
  color: ${BouColor.TOOL_TEXT_DARKER_GRAY};
  border-top: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  line-height: 1.3;
  font-style: italic;

  & + & {
    border-left: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  }
  p + p {
    margin-top: 0.4em;
  }

  li:not(:last-child) {
    margin-bottom: 0.3em;
  }
`;
